import { useMemo, useCallback } from 'react';
import { useClients } from './useClients';
import { useClientStatusChange } from './useClientStatusChange';
import { Client } from '@/types/client';
import { toast } from 'sonner';

interface ColumnData {
  id: string;
  title: string;
  status: Client['status'];
  color: string;
  bgColor: string;
}

// Follow-up statuses that should be shown in the Kanban board
const followUpStatuses: Client['status'][] = [
  'confirmed',
  'waiting',
  'in_progress', 
  'waiting_supplier',
  'pricing_requested',
  'pricing_sent',
  'booking_confirmed',
  'traveling',
  'customer_service'
];

const defaultColumns: ColumnData[] = [
  {
    id: 'confirmed',
    title: 'مؤكد',
    status: 'confirmed',
    color: '#10b981',
    bgColor: 'bg-green-500',
  },
  {
    id: 'waiting',
    title: 'في الانتظار',
    status: 'waiting',
    color: '#f59e0b',
    bgColor: 'bg-yellow-500',
  },
  {
    id: 'in_progress',
    title: 'قيد التنفيذ',
    status: 'in_progress',
    color: '#3b82f6',
    bgColor: 'bg-blue-500',
  },
  {
    id: 'waiting_supplier',
    title: 'انتظار المورد',
    status: 'waiting_supplier',
    color: '#f97316',
    bgColor: 'bg-orange-500',
  },
  {
    id: 'pricing_requested',
    title: 'طلب تسعير',
    status: 'pricing_requested',
    color: '#8b5cf6',
    bgColor: 'bg-purple-500',
  },
  {
    id: 'pricing_sent',
    title: 'تم إرسال التسعير',
    status: 'pricing_sent',
    color: '#6366f1',
    bgColor: 'bg-indigo-500',
  },
  {
    id: 'booking_confirmed',
    title: 'تأكيد الحجز',
    status: 'booking_confirmed',
    color: '#14b8a6',
    bgColor: 'bg-teal-500',
  },
  {
    id: 'traveling',
    title: 'في السفر',
    status: 'traveling',
    color: '#06b6d4',
    bgColor: 'bg-cyan-500',
  },
  {
    id: 'customer_service',
    title: 'خدمة العملاء',
    status: 'customer_service',
    color: '#ec4899',
    bgColor: 'bg-pink-500',
  },
];

export const useKanbanLogic = (searchTerm: string, statusFilter: string | null) => {
  const { data: allClients, isLoading, error } = useClients();
  const updateClientStatusMutation = useClientStatusChange();

  // Filter clients
  const filteredClients = useMemo(() => {
    if (!allClients) return [];

    let filtered = allClients.filter(client => 
      followUpStatuses.includes(client.status)
    );

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(client =>
        client.name_ar.toLowerCase().includes(searchLower) ||
        client.name_en?.toLowerCase().includes(searchLower) ||
        client.mobile?.includes(searchTerm) ||
        client.company?.toLowerCase().includes(searchLower) ||
        client.additional_info?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(client => client.status === statusFilter);
    }

    return filtered;
  }, [allClients, searchTerm, statusFilter]);

  // Group clients by status
  const groupedClients = useMemo(() => {
    const grouped: Record<Client['status'], Client[]> = {} as Record<Client['status'], Client[]>;
    
    // Initialize all columns with empty arrays
    defaultColumns.forEach(column => {
      grouped[column.status] = [];
    });

    // Group filtered clients
    filteredClients.forEach(client => {
      if (grouped[client.status]) {
        grouped[client.status].push(client);
      }
    });

    // Sort clients within each group by creation date (newest first)
    Object.keys(grouped).forEach(status => {
      grouped[status as Client['status']].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });

    return grouped;
  }, [filteredClients]);

  // Update client status
  const updateClientStatus = useCallback(async (clientId: string, newStatus: Client['status']) => {
    try {
      console.log('🚀 Updating client status:', { clientId, newStatus });
      
      await updateClientStatusMutation.mutateAsync({
        clientId,
        newStatus,
      });

      const statusLabel = defaultColumns.find(col => col.status === newStatus)?.title || newStatus;
      toast.success(`تم تحديث حالة العميل إلى "${statusLabel}"`);
      
    } catch (error) {
      console.error('❌ Failed to update client status:', error);
      toast.error('فشل في تحديث حالة العميل');
    }
  }, [updateClientStatusMutation]);

  // Reset layout (could be used for future column customization)
  const resetLayout = useCallback(() => {
    toast.success('تم إعادة تعيين التخطيط');
  }, []);

  // Get column by status
  const getColumnByStatus = useCallback((status: Client['status']) => {
    return defaultColumns.find(col => col.status === status);
  }, []);

  // Get clients in a specific column
  const getClientsInColumn = useCallback((status: Client['status']) => {
    return groupedClients[status] || [];
  }, [groupedClients]);

  return {
    // Data
    columns: defaultColumns,
    allClients: allClients || [],
    filteredClients,
    groupedClients,
    
    // Loading states
    isLoading,
    error,
    isUpdating: updateClientStatusMutation.isPending,
    
    // Actions
    updateClientStatus,
    resetLayout,
    getColumnByStatus,
    getClientsInColumn,
  };
};